"use client";

import {setMobileQueueScope} from '@/lib/mobile/queue-scope';
import {useRouter} from 'next/navigation';
import { useEffect,useLayoutEffect } from "react";

import { markMobileEvidenceUploaded, queueMobileAction, readMobileActions, readMobileEvidence, removeMobileActions, type MobileAction, type MobileEvidence } from "@/lib/mobile/offline-queue";
import { prepareMobileActionsForSync } from "@/lib/mobile/offline-proof-sync";

const deviceKeyStorage = "threadops:mobile:device-key:v1";
const sessionStorage = "threadops:mobile:session-id:v1";

export function MobileRuntime({ scope, serviceWorkerEnabled = true }: { scope: string | null; serviceWorkerEnabled?: boolean }) {
  const router=useRouter();
  useLayoutEffect(()=>{setMobileQueueScope(resolveScope(scope));return ()=>setMobileQueueScope(null);},[scope]);
  useEffect(() => {
    if (!("indexedDB" in window) || !window.isSecureContext) return;
    let stopped = false, syncing=false, rerun=false;
    // An offline shell can use the last account on this device; replay still
    // requires that same authenticated account/company on the server.
    const effectiveScope=resolveScope(scope);
    setMobileQueueScope(effectiveScope);
    try{if(scope)window.localStorage.setItem('threadops:mobile:last-scope:v1',scope);}catch{/* Offline storage may be unavailable. */}
    const scopedSessionStorage=`${sessionStorage}:${effectiveScope}`;

    if ("serviceWorker" in navigator) {
      if (serviceWorkerEnabled) {
        navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).catch(() => undefined);
      } else {
        void removeDevelopmentServiceWorker();
      }
    }

    async function start() {
      if(!effectiveScope)return;
      try {
        const deviceKey = getDeviceKey();
        const response = await fetch("/api/mobile/session", { body: JSON.stringify({ appVersion: "web-1", deviceKey, displayName: browserName(), platform: platform(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" }), headers: { "content-type": "application/json" }, method: "POST" });
        if (!response.ok || stopped) return;
        const state = await response.json() as { sessionId: string; scope: string };
        if(state.scope!==effectiveScope||stopped)return;
        window.localStorage.setItem(scopedSessionStorage, state.sessionId);
        await queueMobileAction("mobile.telemetry", { appVersion: "web-1", online: navigator.onLine });
        await queueMobileAction("mobile.permissions", await permissionSnapshot());
        await sync();
      } catch {
        // Offline startup is expected; the online event retries queued work later.
      }
    }

    async function sync() {
      if (!navigator.onLine || stopped || !effectiveScope) return;
      if(syncing){rerun=true;return;}
      let sessionId:string|null=null;try{sessionId=window.localStorage.getItem(scopedSessionStorage);}catch{return;}
      if (!sessionId) return;
      syncing=true;
      try {
        const queuedActions = await readMobileActions();
        const actions = await prepareMobileActionsForSync(queuedActions, { markUploaded: markMobileEvidenceUploaded, readEvidence: readMobileEvidence, uploadEvidence: (action, evidence) => action.type === "safety.form_submission" ? uploadSafetyFormEvidence(evidence) : uploadEvidence(sessionId, action, evidence) });
        if (!actions.length || stopped) return;
        const formActions = actions.filter((action) => action.type === "safety.form_submission");
        const contractorActions = actions.filter((action) => action.type.startsWith("contractor.muster_"));
        const mobileActions = actions.filter((action) => action.type !== "safety.form_submission" && !action.type.startsWith("contractor.muster_"));
        const completedIds: string[] = [];
        if (formActions.length) {
          const response = await fetch("/api/safety/forms/sync", { body: JSON.stringify({ actions: formActions }), headers: { "content-type": "application/json" }, method: "POST" });
          if (response.status === 401 || response.status === 403) { if(stopped)return; window.localStorage.removeItem(scopedSessionStorage); window.location.assign("/auth/sign-out?status=deviceRevoked"); return; }
          if (response.ok) completedIds.push(...terminalResultIds(await response.json()));
        }
        if (contractorActions.length) {
          const response = await fetch("/api/contractors/muster/sync", { body: JSON.stringify({ actions: contractorActions }), headers: { "content-type": "application/json" }, method: "POST" });
          if (response.status === 401 || response.status === 403) { if(stopped)return; window.localStorage.removeItem(scopedSessionStorage); window.location.assign("/auth/sign-out?status=deviceRevoked"); return; }
          if (response.ok) completedIds.push(...terminalResultIds(await response.json()));
        }
        if (mobileActions.length) {
          const response = await fetch("/api/mobile/sync", { body: JSON.stringify({ actions: mobileActions, sessionId }), headers: { "content-type": "application/json" }, method: "POST" });
          if (response.status === 401 || response.status === 403) { if(stopped)return; window.localStorage.removeItem(scopedSessionStorage); window.location.assign("/auth/sign-out?status=deviceRevoked"); return; }
          if (response.ok) completedIds.push(...terminalResultIds(await response.json()));
        }
        await removeMobileActions(completedIds);
        if(!stopped&&actions.some(action=>completedIds.includes(action.id)&&!action.type.startsWith("mobile.")))router.refresh();
        if (completedIds.length) window.dispatchEvent(new CustomEvent("threadops:mobile-actions-synced", { detail: { ids: completedIds } }));
      } catch {
        // Keep the encrypted queue intact until connectivity recovers.
      } finally {
        syncing=false;
        if(rerun&&!stopped){rerun=false;queueMicrotask(()=>void sync());}
      }
    }

    const queued = () => { void sync(); };
    const online=()=>{void start();};
    window.addEventListener("online", online);
    window.addEventListener("threadops:mobile-action-queued", queued);
    void start();
    return () => { stopped = true; window.removeEventListener("online", online); window.removeEventListener("threadops:mobile-action-queued", queued); };
  }, [serviceWorkerEnabled,scope,router]);
  return null;
}

async function removeDevelopmentServiceWorker() {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations
      .filter((registration) => registration.scope.startsWith(window.location.origin))
      .map((registration) => registration.unregister()));
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys
        .filter((key) => key.startsWith("threadops-public-shell-"))
        .map((key) => caches.delete(key)));
    }
  } catch {
    // Development cleanup is best-effort and must never block the application.
  }
}

function terminalResultIds(body: unknown) {
  if (!body || typeof body !== "object" || !("results" in body) || !Array.isArray(body.results)) return [];
  return body.results.flatMap((result) => result && typeof result === "object" && "id" in result && "status" in result
    && typeof result.id === "string" && typeof result.status === "string"
    && ["applied", "conflict", "rejected"].includes(result.status) ? [result.id] : []);
}

async function uploadEvidence(sessionId: string, action: MobileAction, evidence: MobileEvidence) {
  const runId = String(action.payload.runId ?? ""), stopId = String(action.payload.stopId ?? "");
  const form = new FormData();
  form.set("sessionId", sessionId); form.set("runId", runId); form.set("stopId", stopId); form.set("evidenceId", evidence.id);
  form.set("kind", evidence.field === "photoFileId" ? "photo" : "signature"); form.set("file", evidence.file);
  const response = await fetch("/api/mobile/evidence", { body: form, method: "POST" });
  if (response.status === 401 || response.status === 403) throw new Error("Mobile evidence access denied");
  if (!response.ok) throw new Error("Mobile evidence upload failed");
  const body = await response.json() as { fileId?: string };
  if (!body.fileId) throw new Error("Mobile evidence upload did not return a file");
  return body.fileId;
}

async function uploadSafetyFormEvidence(evidence: MobileEvidence) {
  const form = new FormData();
  form.set("fieldKey", evidence.field.replace(/^form:/, ""));
  form.set("file", evidence.file);
  const response = await fetch("/api/safety/forms/evidence", { body: form, method: "POST" });
  if (response.status === 401 || response.status === 403) throw new Error("Safety form evidence access denied");
  if (!response.ok) throw new Error("Safety form evidence upload failed");
  const body = await response.json() as { fileId?: string };
  if (!body.fileId) throw new Error("Safety form evidence upload did not return a file");
  return body.fileId;
}

function getDeviceKey() { const existing = window.localStorage.getItem(deviceKeyStorage); if (existing) return existing; const bytes = crypto.getRandomValues(new Uint8Array(24)); const value = btoa(String.fromCharCode(...bytes)).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", ""); window.localStorage.setItem(deviceKeyStorage, value); return value; }
function platform() { const source = `${navigator.platform} ${navigator.userAgent}`.toLowerCase(); if (source.includes("android")) return "android"; if (source.includes("iphone") || source.includes("ipad")) return "ios"; if (source.includes("win")) return "windows"; if (source.includes("mac")) return "macos"; if (source.includes("linux")) return "linux"; return "other"; }
function browserName() { const source = navigator.userAgent; return `${source.includes("Edg/") ? "Edge" : source.includes("Chrome/") ? "Chrome" : source.includes("Safari/") ? "Safari" : "Browser"} on ${platform()}`; }
async function permissionSnapshot() { const permission = async (name: "camera" | "geolocation" | "notifications") => { try { return (await navigator.permissions.query({ name: name as PermissionName })).state; } catch { return "unavailable"; } }; const [camera, geolocation, notifications] = await Promise.all([permission("camera"), permission("geolocation"), permission("notifications")]); return { camera, geolocation, notifications, online: navigator.onLine }; }

function resolveScope(scope:string|null){
 if(scope)return scope;
 try{return !navigator.onLine?window.localStorage.getItem('threadops:mobile:last-scope:v1'):null;}catch{return null;}
}
